'use client'

import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Check } from 'lucide-react'
import { formatPrice, formatDateLong, formatImageURL } from '@/utils/util'
import Image from 'next/image'
import Lend from '@/assets/lendmn.png'
import moment from 'moment'
import { Button } from '@/components/elements/Button'

interface LendDrawerProps {
  isOpen: boolean
  onClose: () => void
  productPrice: number
  productName: string
  productImage: string
  vendor?: any
}

export default function LendDrawer({ isOpen, onClose, productPrice, productName, productImage, vendor }: LendDrawerProps) {
  const [selectedPlan, setSelectedPlan] = useState<'30days' | '60days' | null>('30days')

  const paymentPlans = {
    '30days': {
      label: '30 хоног',
      description: '1 хуваах',
      installments: 1,
      days: 30
    },
    '60days': {
      label: '60 хоног',
      description: '4 хуваах',
      installments: 4,
      days: 60
    }
  }

  const calculateInstallments = (plan: keyof typeof paymentPlans) => {
    const payments = paymentPlans[plan].installments
    const days = paymentPlans[plan].days
    const monthlyAmount = Math.ceil(productPrice / payments)
    const totalAmount = monthlyAmount * payments
    const fee = totalAmount - productPrice
    const feePercentage = ((fee / productPrice) * 100).toFixed(1)

    return {
      payments,
      days,
      monthlyAmount,
      totalAmount,
      fee,
      feePercentage: parseFloat(feePercentage)
    }
  }

  const getPaymentDates = (plan: keyof typeof paymentPlans) => {
    const today = moment()
    const payments = paymentPlans[plan].installments
    const dayInterval = paymentPlans[plan].days / payments

    return Array.from({ length: payments }, (_, index) => {
      if (index === 0) {
        return today.toDate()
      }
      return today.clone().add(index * dayInterval, 'days').toDate()
    })
  }

  const FillingCircle = ({ fillPercentage, paymentCount, size = 48 }: { fillPercentage: number, paymentCount: number, size?: number }) => {
    const radius = (size - 8) / 2
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (fillPercentage / 100) * circumference

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#ffc299"
            strokeWidth="4"
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#ff7300"
            strokeWidth="4"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-in-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold" style={{ color: '#ff7300' }}>
            {paymentCount}
          </span>
        </div>
      </div>
    )
  }

  const CircularProgress = ({ percentage, size = 48 }: { percentage: number, size?: number }) => {
    const radius = (size - 8) / 2
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (percentage / 100) * circumference

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size} >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="4"
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#ff7300"
            strokeWidth="4"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-300 ease-in-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-semibold" style={{ color: '#ff7300' }}>
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
    )
  }

  const PaymentSchedule = ({ plan }: { plan: keyof typeof paymentPlans }) => {
    const calculation = calculateInstallments(plan)
    const paymentDates = getPaymentDates(plan)

    return (
      <div className="space-y-3">
        {paymentDates.map((date, index) => {
          const progress = ((index + 1) / calculation.payments) * 100
          const isLastPayment = index === calculation.payments - 1
          const amount = isLastPayment ? productPrice - (calculation.monthlyAmount * (calculation.payments - 1)) : calculation.monthlyAmount

          const isToday = index === 0

          return (
            <div key={index} className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <CircularProgress percentage={progress} size={48} />
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">
                  {index === 0 ? 'Эхний төлөлт' : `${index + 1}-р төлөлт`}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isToday ? 'Өнөөдөр' : formatDateLong(date)}
                </div>
                <div className="text-base font-semibold text-foreground mt-1">
                  {formatPrice(amount)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 flex justify-end">
          <TransitionChild
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="relative flex w-full max-w-115 flex-col overflow-hidden bg-white shadow-2xl rounded-l-2xl">
              <div className="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 border-b border-border bg-white/95 px-4 backdrop-blur">
                <div className="flex min-w-0 items-center gap-3">
                  <Image src={Lend} alt="Lend" className="h-5 w-fit object-cover" />
                  <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase leading-none tracking-[0.14em] text-muted-foreground">Хуваан төлөлт</div>
                    <div className="truncate font-gilroy text-[15px] font-semibold leading-none tracking-[-0.005em] text-foreground">LendDy үйлчилгээ</div>
                  </div>
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-6 pt-6">
                <div className="mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative w-24 h-24 shrink-0">
                      <img src={formatImageURL(productImage)} alt={productName} className="w-full h-full rounded-lg object-cover object-center" />
                    </div>
                    <div>
                      <h3 className="font-medium text-base leading-6 text-foreground line-clamp-2 wrap-break-word">{productName}</h3>
                      <p className="text-base text-foreground font-medium leading-6 mt-1">{formatPrice(productPrice)}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <h3 className="text-base font-medium text-foreground">Хуваан төлөх нөхцөл</h3>

                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(paymentPlans).map(([planKey, plan]) => {
                      const isSelected = selectedPlan === planKey
                      const fillPercentage = 100 / plan.installments

                      return (
                        <div key={planKey} className={`border rounded-lg px-4 py-3 cursor-pointer transition-colors ${isSelected ? 'bg-orange-50' : 'border-input hover:bg-muted transition-colors'}`} style={isSelected ? { borderColor: '#ff7300' } : {}} onClick={() => setSelectedPlan(planKey as keyof typeof paymentPlans)}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex flex-col">
                              <div className="font-medium text-foreground">{plan.label}</div>
                              <div className="text-xs text-muted-foreground">{plan.description}</div>
                            </div>
                            <FillingCircle fillPercentage={fillPercentage} paymentCount={plan.installments} size={36} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {selectedPlan && (
                  <div className="mb-6">
                    <h4 className="text-base font-medium text-foreground mb-4">
                      {paymentPlans[selectedPlan].label}т {paymentPlans[selectedPlan].installments} хувааж төлөх
                    </h4>
                    <PaymentSchedule plan={selectedPlan} />
                  </div>
                )}

                <div className="text-xs text-muted-foreground mb-6 text-justify">
                  <ul className="space-y-2 list-disc ml-0 pl-0">
                    <li className="ml-4">100,000₮-өөс дээш үнэтэй худалдан авалтад Lend зээлийн үйлчилгээ ашиглах боломжтой.</li>
                    <li className="ml-4">Төлбөрийн сонголтыг LendDy гэж сонгоно.</li>
                    <li className="ml-4">LendMN аппликэйшнээс зээлийн үүссэн нэхэмжлэлийг зөвшөөрснөөр гүйлгээ амжилттай болно.</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border bg-white p-3">
                <span className="text-[12px] text-muted-foreground">{vendor?.createdAt ? `Дэлгүүр нээгдсэн огноо: ${formatDateLong(new Date(vendor.createdAt))}` : ''}</span>
                <Button tag="button" size="sm" color="amber-500" border={vendor?.border} icon={Check} iconPosition="right" onClick={onClose}>Ойлголоо</Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}